import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlag, faExclamationTriangle, faBook } from '@fortawesome/free-solid-svg-icons';
import { faFlag as farFlag } from '@fortawesome/free-regular-svg-icons';
import Tooltip from '@mui/material/Tooltip';
import moment from 'moment';

import Field from './Field';
import Change from './Change';
import Loading from '../components/Loading';
import Section from '../components/Section';
import Tag from '../components/Tag';

import api from '../util/api';
import utils from '../util/utils';
import login from '../util/login';
import tags from '../util/tags';

import wowheadIcon from '../icons/wowhead.png';

export default class Item extends Component {
    constructor(props) {
        super(props);
        this.state = {
            edit: login.contributor(),
            error: false,
            ids: [],
            multiple: false,
            tags: [],
            tagsuggestions: [],
            seen: null,
            history: null,
            questlog: null,
            delta: {}
        };
    }

    componentDidMount() {
        this.initItem();
    }

    componentDidUpdate(previous) {
        if (this.props.category === previous.category && this.props.id === previous.id) return;
        this.initItem();
    }

    initItem = async () => {
        const ids = this.props.id.split('.').map(i => parseInt(i));
        const multiple = ids.length > 1;
        this.setState({ seen: null, history: null, questlog: null, ids, multiple });

        if (!multiple && login.jwt.get()) {
            tags.get(this.props.category, ids[0]).then(tags =>
                this.setState({ tags: tags.map(tag => ({ value: tag, label: tag })) })
            ).catch(e =>
                alert('Could not download tags from the server, please try again')
            );

            tags.existing().then(tags =>
                this.setState({ tagsuggestions: tags.map(tag => ({ value: tag, label: tag })) })
            );
        }

        if (!multiple && login.contributor()) {
            try {
                const response = await api.get('contrib/' + encodeURIComponent(this.props.category) + '/' + encodeURIComponent(this.props.id) + '?jwt=' + encodeURIComponent(login.jwt.get()));
                const json = await response.json();

                const questlog = json.questlog || [];
                questlog.sort((a, b) => b.found - a.found);

                this.setState({ seen: json.seen, history: json.history || [], questlog });
            } catch(e) {
                // can't show seen by
                console.error(e);
            }
        }
    }

    fnEdit = () => {
        if (!this.state.edit && !login.jwt.get()) {
            alert('Please log in via Battle.net before reporting any feedback.');
            return;
        }

        this.setState({ edit: !this.state.edit, delta: {} });
    }

    handleChange = (id, value) => {
        if (id === 'missing') value = (value === 'true'); // remove nulls
        if (id === 'excluded') value = (value === 'true'); // remove nulls

        if (id.startsWith('grant')) {
            value = value.split(',').map(i => parseInt(i)).filter(i => i);
        }

        if (value === '') value = null;

        this.setState({ delta: { ...this.state.delta, [id]: value } });
    }

    handleIconChange = (id, value) => {
        this.handleChange(id, value);
        this.setState({ icon: utils.icon(value) });
    }

    handleTagsChange = (id, value) => {
        if (id === 'tags') {
            // assume change will succeed
            const prev = this.state.tags;
            this.setState({ tags: value });

            // save to serverStorage
            tags.set(this.props.category, this.state.ids, value.map(v => v.value)).catch(e => {
                // revert state on error
                this.setState({ tags: prev });

                if (e?.cause === 'tags') {
                    alert(e.message);
                } else {
                    alert('Could not save tags to the server, please try again');
                }
            });
        } else {
            // convert value to array of ids
            this.handleChange(id, value?.map(v => v.value));
        }
    }

    fnSubmit = async () => {
        const corrections = this.state.delta;
        if (!corrections || !Object.keys(corrections)?.length) {
            return this.setState({ error: 'Error: No changes to submit' });
        }

        this.setState({ error: null });

        const payload = { id: this.state.ids, type: this.props.category.toLowerCase(), corrections };
        const response = await api.post('feedback?jwt=' + encodeURIComponent(login.jwt.get()), payload);
        if (response.status === 204) {
            this.setState({ edit: false });
        } else {
            this.setState({ error: 'Error' });
        }
    }

    render() {
        document.title = 'Item Details | Data for Azeroth | World of Warcraft Leaderboards for Collectors';

        const tpc = this.props.collection;

        let collection = null;
        let title = null;
        let showSource = false;
        let showIcon = false;
        let showCovenant = false;
        let showCategory = false;
        let showProfession = false;
        let showClass = false;
        let showGrants = false;
        let showSeen = false;

        if (tpc.showAchievements) {
            collection = tpc.achievements;
            title = 'Achievement';
            showIcon = true;
            showCovenant = true;
            showCategory = true;
            showGrants = true;
            showSeen = true;
        } else if (tpc.showMounts) {
            collection = tpc.mounts;
            title = 'Mount';
            showSource = true;
            showIcon = true;
            showProfession = true;
            showClass = true;
            showGrants = true;
            showSeen = true;
        } else if (tpc.showPets) {
            collection = tpc.pets;
            title = 'Pet';
            showSource = true;
            showIcon = true;
            showGrants = true;
            showSeen = true;
        } else if (tpc.showTitles) {
            collection = tpc.titles;
            title = 'Title';
            showSource = true;
            showClass = true;
            showGrants = true;
            showSeen = true;
        } else if (tpc.showRecipes) {
            collection = tpc.recipes;
            title = 'Recipe';
            showSource = true;
            showIcon = true;
            showProfession = true;
            showGrants = true;
            showSeen = true;
        } else if (tpc.showReputations) {
            collection = tpc.reputations;
            title = 'Reputation';
            showCovenant = true;
            showGrants = true;
            showSeen = true;
        } else if (tpc.showQuests) {
            collection = tpc.quests;
            title = 'Quest';
            showCovenant = true;
            showCategory = true;
            showProfession = true;
            showGrants = true;
            showSeen = true;
        } else if (tpc.showToys) {
            collection = tpc.toys;
            title = 'Toy';
            showSource = true;
            showIcon = true;
            showGrants = true;
            showSeen = true;
        } else if (tpc.showAppearances) {
            collection = tpc.appearances;
            title = 'Appearance';
            showIcon = true;
        } else if (tpc.showAppearancesources) {
            collection = tpc.appearancesources;
            title = 'Appearance Source';
            showIcon = true;
        } else if (tpc.showHeirlooms) {
            collection = tpc.heirlooms;
            title = 'Heirloom';
            showSource = true;
            showIcon = true;
        } else if (tpc.showHunterpets) {
            collection = tpc.hunterpets;
            title = 'Hunter Pet';
        } else if (tpc.showSoulshapes) {
            collection = tpc.soulshapes;
            title = 'Soulshape';
            showSource = true;
            showIcon = true;
        } else if (tpc.showSkips) {
            collection = tpc.skips;
            title = 'Raid Skip';
            showIcon = true;
            showCategory = true;
        } else if (tpc.showManuscripts) {
            collection = tpc.manuscripts;
            title = 'Manuscript';
            showSource = true;
            showIcon = true;
        }

        if (!collection) return <Loading message="Loading"/>;

        const idset = new Set(this.state.ids);
        const items = collection.filter(item => idset.has(item.id));
        const item = this.state.multiple ? {} : items[0];
        if (!item) return <Loading message="Item Not Found"/>;

        if (!this.state.multiple) {
            const canonical = '/collections/' + encodeURIComponent(this.props.category) + '/' + encodeURIComponent(this.props.id) + '/' + encodeURIComponent(utils.canonical(item.name));
            if (this.props.history.location.pathname !== canonical) {
                window.setTimeout(() => this.props.history.push(canonical), 10);
            }

            document.title = item.name + ' | ' + title + ' Details | Data for Azeroth | World of Warcraft Leaderboards for Collectors';
        }

        const buttons =
            <div className={'my-auto form-inline ' + (window.bootstrap.md.matches ? 'ml-auto' : 'd-flex')}>
                <div className={window.bootstrap.md.matches ? '' : 'mr-auto'}> </div>
                <div>
                    {this.state.edit && <button type="button" className="btn btn-primary mr-1" onClick={this.fnSubmit}>Submit</button>}
                    {this.state.error && <Loading message={this.state.error} />}
                    {login.contributor() ? <Tooltip title="Report Inaccuracy"><button type="button" className="btn btn-primary" onClick={this.fnEdit}><FontAwesomeIcon icon={this.state.edit ? faFlag : farFlag} /></button></Tooltip> : null}
                </div>
            </div>;

        const icons =
            <div>
                <a href={utils.wowhead(item.field, item.wowheadid, item.id)} target="_blank" rel="noopener noreferrer"><img src={wowheadIcon} alt="Wowhead Link" style={{ height: '1.5rem', width: '1.5rem' }} /></a>
            </div>;

        const footer = <>Most data originates from Blizzard's API, but must be manually reviewed and corrected by <i>Data for Azeroth</i>.</>;
        const seendate = (typeof item.seen === 'number') ? moment.utc(item.seen).format('ll') : 'Never';
        const seenby = this.state.seen?.seenby ? <>(by <Link to={'/characters/' + this.state.seen.seenby.replaceAll('#', '/')} className={'class-' + this.state.seen.class}>{this.state.seen.name || this.state.seen.seenby}</Link>)</> : null;
        const questlog = this.state.questlog?.map(log => <div>{moment.utc(1000 * log.found).format('ll')} (<Link to={'/characters/' + log.character?.replaceAll('#', '/')}>{log.character}</Link> Quest Log)</div>); // temp
        const seen = <>{seendate} {seenby} {questlog}</>;

        return (
            <>
                <Section title={this.state.multiple ? '[' + items.length + ' Items]' : item.name} subtitle={title} icon={this.state.icon || utils.icon(item.icon) || 'https://render.worldofwarcraft.com/us/icons/56/inv_letter_03.jpg'} buttons={buttons} icons={icons} footer={footer}>
                    {this.state.multiple ?
                        <>
                        <div className="p-2 rounded rankings"><FontAwesomeIcon icon={faExclamationTriangle} /> Any changes will apply to all {items.length} items below</div>
                        <div className="collection collection-nosplit mx-0 my-3">
                            {items.map(item => <Tag key={item.id} item={item} size="col-12 col-md-6 col-lg-4 col-xl-3" showCollected={false} showDetails={false} removeLink={this.state.ids.filter(e => e !== item.id).join('.')} />)}
                        </div>
                        </>
                    : null}

                    {!this.state.multiple && login.jwt.get() ?
                        <Field edit={true} visible={true} name="Tags" value={this.state.tags} options={this.state.tagsuggestions} tags={true} id="tags" onChange={this.handleTagsChange}>
                            Add your own tags to this item, which can be used in filters
                        </Field>
                    : null}

                    <Field edit={false} visible={!this.state.multiple} name="Blizzard API ID" value={item.id}></Field>

                    <Field edit={this.state.edit} visible={true} name="Name" value={item.name} id="name" onChange={this.handleChange}></Field>
                    <Field edit={this.state.edit} visible={showIcon} name="Icon" value={item.icon} id="icon" onChange={this.handleIconChange}></Field>
                    <Field edit={this.state.edit} visible={tpc.showAchievements} name="Achievement Points" value={item.points} id="points" onChange={this.handleChange}></Field>
                    <Field edit={this.state.edit} visible={showCategory} name="Category" value={item.category} id="category" onChange={this.handleChange}></Field>
                    <Field edit={this.state.edit} visible={tpc.showAchievements} name="Sub-Category" value={item.subcategory} id="subcategory" onChange={this.handleChange}></Field>

                    <Field edit={this.state.edit} visible={tpc.showQuests} name="Type" value={item.type} options={utils.questtypes} id="type" onChange={this.handleChange}></Field>
                    <Field edit={this.state.edit} visible={tpc.showAppearances} name="Slot" value={item.slot} options={utils.slots} id="slot" onChange={this.handleChange}></Field>
                    <Field edit={this.state.edit} visible={tpc.showAppearances} name="Type" value={item.type} options={utils.geartypes} id="type" onChange={this.handleChange}></Field>
                    <Field edit={this.state.edit} visible={tpc.showAppearances} name="Quality" value={item.quality} map={utils.qualities} id="quality" onChange={this.handleChange}></Field>

                    <Field edit={this.state.edit} visible={true} name="Faction" value={item.faction} map={utils.factions} id="faction" onChange={this.handleChange}></Field>
                    <Field edit={this.state.edit} visible={showCovenant} name="Covenant" value={item.covenant} map={utils.covenants} id="covenant" onChange={this.handleChange}></Field>
                    <Field edit={this.state.edit} visible={showClass} name="Class" value={item.class} map={utils.classes} id="class" onChange={this.handleChange}></Field>
                    <Field edit={this.state.edit} visible={showProfession} name={(tpc.showMounts ? 'Required ' : '') + 'Profession'} value={item.prof} options={utils.professions} id="prof" onChange={this.handleChange}></Field>
                    <Field edit={this.state.edit} visible={tpc.showQuests || tpc.showAppearances} name="Classes" multiple={true} value={item.class} map={this.state.edit ? utils.classes.filter(c => c.key !== '') : utils.classes} id="class" onChange={this.handleChange}></Field>
                    <Field edit={this.state.edit} visible={tpc.showQuests || tpc.showAppearances} name="Races" multiple={true} value={item.race} map={this.state.edit ? utils.races.filter(c => c.key !== '') : utils.races} id="race" onChange={this.handleChange}></Field>

                    <Field edit={this.state.edit} visible={showSource} name="Source Type (Icon)" value={item.sourceicon} options={utils.sourceicons} id="sourceicon" onChange={this.handleChange} />
                    <Field edit={this.state.edit} visible={showSource} name="Source" value={item.source} id="source" onChange={this.handleChange} />
                    <Field edit={this.state.edit} visible={showSource} name="Source Zone" value={item.sourcezone} id="sourcezone" onChange={this.handleChange} />
                    <Field edit={this.state.edit} visible={showSource} name="Source Currency" value={item.sourcecurrency} id="sourcecurrency" onChange={this.handleChange} />
                    <Field edit={this.state.edit} visible={showSource} name="Source Price" value={item.sourceprice} id="sourceprice" onChange={this.handleChange} />
                    <Field edit={this.state.edit} visible={showSource} name="Source Faction" value={item.sourcefaction} id="sourcefaction" onChange={this.handleChange} />
                    <Field edit={this.state.edit} visible={showSource} name="Source Standing" value={item.sourcestanding} id="sourcestanding" onChange={this.handleChange} />

                    <Field edit={this.state.edit} visible={tpc.showRecipes} name="Binds" value={item.bind} id="bind" map={utils.binds} onChange={this.handleChange} />

                    <Field edit={this.state.edit} visible={true} name="Patch" value={item.patch} options={utils.patches.all} id="patch" onChange={this.handleChange}>
                        Patch that this item was first detected in Blizzard's API
                    </Field>

                    <Field edit={this.state.edit} visible={true} name="Unobtainable" value={item.unobtainable} map={utils.unobtainables} id="unobtainable" onChange={this.handleChange}>
                        Indicates whether this item can currently be obtained in-game
                    </Field>

                    <Field edit={this.state.edit} visible={true} name="Missing" value={item.missing} map={utils.noyes} id="missing" onChange={this.handleChange}>
                        Indicates whether this item is missing from Blizzard's API
                    </Field>

                    <Field edit={this.state.edit} visible={true} name="Excluded" value={item.excluded} map={utils.noyes} id="excluded" onChange={this.handleChange}>
                        Indicates whether this item is excluded from rankings, such as repeatable quests, temporary titles, garbage data, etc.
                    </Field>

                    <Field edit={this.state.edit} visible={true} name="Note" value={item.note} id="note" onChange={this.handleChange}>
                        Additional information from <i>Data for Azeroth</i>
                    </Field>

                    <Field edit={false} visible={!this.state.multiple && (typeof item.rarity === 'number')} name="Rarity" value={(typeof item.rarity === 'number') ? utils.toFixed(item.rarity, 4) + '%' : ''}>
                        The percentage of players tracked by <i>Data for Azeroth</i> that have obtained this item
                    </Field>

                    <Field edit={false} visible={showSeen && !this.state.multiple} name="Seen" value={seen}>
                        The most recent date <i>Data for Azeroth</i> detected a player obtained this item
                    </Field>
                </Section>

                {showGrants ?
                    <Section title="Grants Credit" footer="Indicates additional credit that will be granted, to fix Blizzard's data" close={login.contributor() ? <Link to="/grants">Summary<FontAwesomeIcon icon={faBook} className="ml-2" /></Link> : null}>
                        <Field edit={this.state.edit} visible={true} name="Achievement IDs" multiple={true} value={item.grantachievements} id="grantachievements" onChange={this.handleChange}></Field>
                        <Field edit={this.state.edit} visible={true} name="Mount IDs" multiple={true} value={item.grantmounts} id="grantmounts" onChange={this.handleChange}></Field>
                        <Field edit={this.state.edit} visible={true} name="Pet IDs" multiple={true} value={item.grantpets} id="grantpets" onChange={this.handleChange}></Field>
                        <Field edit={this.state.edit} visible={true} name="Quest IDs" multiple={true} value={item.grantquests} id="grantquests" onChange={this.handleChange}></Field>
                        <Field edit={this.state.edit} visible={true} name="Recipe IDs" multiple={true} value={item.grantrecipes} id="grantrecipes" onChange={this.handleChange}></Field>
                        <Field edit={this.state.edit} visible={true} name="Reputation IDs" multiple={true} value={item.grantreputations} id="grantreputations" onChange={this.handleChange}></Field>
                        <Field edit={this.state.edit} visible={true} name="Title IDs" multiple={true} value={item.granttitles} id="granttitles" onChange={this.handleChange}></Field>
                        <Field edit={this.state.edit} visible={true} name="Toy IDs" multiple={true} value={item.granttoys} id="granttoys" onChange={this.handleChange}></Field>
                    </Section>
                : null}

                {this.state.history ?
                    <Section title="Change Log" close={<Link to={'/collections/' + this.props.category + '/log'}>View All<FontAwesomeIcon icon={faBook} className="ml-2" /></Link>} padding="off">
                        <table className="table table-hover m-0">
                            <tbody>
                                {this.state.history.map((row, idx) => <Change key={idx} changes={row} />)}
                            </tbody>
                        </table>
                    </Section>
                : null}
            </>
        );
    }
}